import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import centreVoipService from '../services/centreVoip.service';
import { CentreCall } from '../services/centre.service';
import { ActiveCallView } from './useWhatsAppCall';
import { stopRingback, unlockCentreNotifSound } from '../utils/centreNotifSound';
import { clearIncomingCallLink, peekIncomingCallLink } from '../utils/incomingCallLink';
import { bindSocketTokenRefresh } from '../utils/socketAuth';
import { isNativeAndroidApp } from '../marketing/utils/isInstalledApp';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api/v1', '') : 'http://localhost:5000');

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

const callerLabel = (call: { remoteCallerNumber?: string; options?: { remoteCallerNumber?: string } }) => {
  const raw = call.remoteCallerNumber || call.options?.remoteCallerNumber || '';
  const user = String(raw).match(/sip:([^@;>]+)/i)?.[1] || raw;
  return String(user).trim();
};

type TelnyxCall = {
  id?: string;
  state?: string;
  cause?: string;
  sipCode?: number;
  remoteCallerNumber?: string;
  remoteStream?: MediaStream;
  options?: { remoteCallerNumber?: string };
  telnyxIDs?: {
    telnyxCallControlId?: string;
    telnyxCallLegId?: string;
    telnyxSessionId?: string;
    telnyxCallSessionId?: string;
  };
  answer: (opts?: { remoteElement?: HTMLAudioElement }) => void | Promise<void>;
  hangup: () => void;
  muteAudio: () => void;
  unmuteAudio: () => void;
  hold?: () => void;
  unhold?: () => void;
};

type TelnyxClient = {
  connect: () => Promise<void> | void;
  disconnect: () => void;
  connected?: boolean;
  remoteElement?: HTMLAudioElement | string;
  newCall: (opts: Record<string, unknown>) => TelnyxCall;
  on: (event: string, handler: (payload?: any) => void) => void;
  off?: (event: string, handler?: (payload?: any) => void) => void;
};

interface UseTelnyxCallArgs {
  storeId: number | null;
  accessToken: string | null;
  enabled: boolean;
  onError?: (message: string) => void;
}

export const useTelnyxCall = ({ storeId, accessToken, enabled, onError }: UseTelnyxCallArgs) => {
  const [view, setView] = useState<ActiveCallView | null>(null);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const clientRef = useRef<TelnyxClient | null>(null);
  const sdkCallRef = useRef<TelnyxCall | null>(null);
  const callIdRef = useRef<number | null>(null);
  const pendingAccept = useRef(false);
  const acceptingRef = useRef(false);
  const autoAnswerRef = useRef(false);
  const serverDialRef = useRef(false);
  const localInboundRef = useRef(false);
  const waitCustomerRef = useRef(false);
  const localHangupRef = useRef(false);
  const remoteEl = useRef<HTMLAudioElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const readyRef = useRef(false);
  const connectingRef = useRef<Promise<TelnyxClient | null> | null>(null);
  const boundStoreRef = useRef<number | null>(null);

  const audioEl = useCallback(() => {
    if (!remoteEl.current) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.setAttribute('playsinline', 'true');
      el.volume = 1;
      el.dataset.voipRemote = '1';
      document.body.appendChild(el);
      remoteEl.current = el;
    }
    return remoteEl.current;
  }, []);

  const playRemote = useCallback((call?: TelnyxCall) => {
    const el = audioEl();
    const stream = call?.remoteStream;
    if (!stream) return;
    el.muted = false;
    el.volume = 1;
    if (el.srcObject !== stream) {
      el.src = '';
      el.srcObject = stream;
      stream.onaddtrack = () => {
        el.srcObject = stream;
        const again = el.play();
        if (again) again.catch(() => {});
      };
    }
    const pending = el.play();
    if (pending) pending.catch(() => {});
  }, [audioEl]);

  const primeAudio = useCallback(() => {
    const el = audioEl();
    if (el.srcObject) return;
    el.src = SILENT_WAV;
    const pending = el.play();
    if (pending) pending.catch(() => {});
  }, [audioEl]);

  const telnyxIds = useCallback((call: TelnyxCall) => {
    const controlId = call.telnyxIDs?.telnyxCallControlId || call.id;
    if (!controlId) return {};
    return {
      callControlId: controlId,
      callLegId: call.telnyxIDs?.telnyxCallLegId,
      callSessionId: call.telnyxIDs?.telnyxSessionId || call.telnyxIDs?.telnyxCallSessionId
    };
  }, []);

  const unlockMic = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const dropClient = useCallback(() => {
    try {
      clientRef.current?.disconnect();
    } catch {
      /* already closed */
    }
    clientRef.current = null;
    readyRef.current = false;
    connectingRef.current = null;
    boundStoreRef.current = null;
  }, []);

  const reset = useCallback(() => {
    pendingAccept.current = false;
    acceptingRef.current = false;
    autoAnswerRef.current = false;
    serverDialRef.current = false;
    localInboundRef.current = false;
    waitCustomerRef.current = false;
    localHangupRef.current = false;
    sdkCallRef.current = null;
    callIdRef.current = null;
    stopRingback();
    setMuted(false);
    setHeld(false);
    setView(null);
    if (remoteEl.current) remoteEl.current.srcObject = null;
  }, []);

  const ensureClient = useCallback(async (): Promise<TelnyxClient | null> => {
    if (!enabled || !storeId) return null;
    if (boundStoreRef.current !== storeId) {
      dropClient();
      boundStoreRef.current = storeId;
    }
    if (clientRef.current && readyRef.current) return clientRef.current;
    if (connectingRef.current) return connectingRef.current;

    connectingRef.current = (async () => {
      const creds = await centreVoipService.getToken(storeId);
      await import('webrtc-adapter');
      const mod = await import('@telnyx/webrtc');
      const TelnyxRTC = (mod as { TelnyxRTC: new (opts: Record<string, unknown>) => TelnyxClient }).TelnyxRTC;
      const client = new TelnyxRTC({
        login_token: creds.token,
        ringtoneFile: undefined,
        remoteElement: audioEl()
      });
      client.remoteElement = audioEl();
      clientRef.current = client;

      await new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('Connexion voix expirée')), 12000);
        client.on('telnyx.ready', () => {
          window.clearTimeout(timer);
          readyRef.current = true;
          resolve();
        });
        client.on('telnyx.error', (err: { message?: string }) => {
          window.clearTimeout(timer);
          readyRef.current = false;
          reject(new Error(err?.message || 'Connexion voix impossible'));
        });
        client.on('telnyx.socket.close', () => {
          readyRef.current = false;
          if (clientRef.current === client) {
            clientRef.current = null;
          }
        });
        void client.connect();
      });

      client.on('telnyx.notification', (notification: { type?: string; call?: TelnyxCall }) => {
        if (notification?.type && notification.type !== 'callUpdate') return;
        const call = notification?.call;
        if (!call) return;
        if (call.remoteStream) playRemote(call);
        const state = String(call.state || '');
        const inbound = String((call as { direction?: string }).direction || '') === 'inbound';
        if (inbound || state === 'ringing') {
          console.info('[VOIP] Notification', state, callerLabel(call) || '');
        }
        if ((state === 'ringing' || (inbound && state === 'new')) && !sdkCallRef.current) {
          sdkCallRef.current = call;
          if (pendingAccept.current || autoAnswerRef.current) {
            void Promise.resolve(call.answer({ remoteElement: audioEl() }))
              .then(() => playRemote(call))
              .catch(() => {});
            pendingAccept.current = false;
            autoAnswerRef.current = false;
            setView((prev) => (prev
              ? {
                ...prev,
                phase: prev.direction === 'in' || serverDialRef.current ? 'connecting' : 'outgoing'
              }
              : prev));
            return;
          }
          localInboundRef.current = true;
          serverDialRef.current = true;
          if (!callIdRef.current) {
            const phone = callerLabel(call);
            setView({
              id: 0,
              phone: phone || 'Appel',
              name: phone || 'Appel',
              direction: 'in',
              phase: 'incoming',
              provider: 'voip'
            });
          }
          return;
        }
        if (state === 'ringing' && !inbound && !serverDialRef.current) {
          setView((prev) => (
            prev && prev.direction === 'out' && prev.phase !== 'active'
              ? { ...prev, phase: 'outgoing' }
              : prev
          ));
        }
        if (state === 'active') {
          sdkCallRef.current = call;
          playRemote(call);
          const waitingCustomer = waitCustomerRef.current;
          const waitingBridge = serverDialRef.current && !waitingCustomer;
          setView((prev) => {
            if (!prev || waitingBridge) return prev;
            if (waitingCustomer && prev.direction === 'out' && prev.phase !== 'active') {
              return prev.phase === 'outgoing' ? prev : { ...prev, phase: 'connecting', held: false };
            }
            return { ...prev, phase: 'active', held: false };
          });
          const id = callIdRef.current;
          if (id && storeId && !waitingCustomer && !waitingBridge) {
            void centreVoipService.event(storeId, id, { state: 'active', ...telnyxIds(call) });
          }
          return;
        }
        if (state === 'held') {
          setHeld(true);
          setView((prev) => (prev ? { ...prev, held: true } : prev));
          return;
        }
        if (state === 'hangup' || state === 'destroy') {
          if (sdkCallRef.current && call !== sdkCallRef.current && call.id !== sdkCallRef.current.id) {
            return;
          }
          const id = callIdRef.current;
          const sid = storeId;
          const serverDial = serverDialRef.current;
          const cause = call.cause || (call.sipCode ? `SIP ${call.sipCode}` : '');
          if (acceptingRef.current && /outbound call/i.test(cause)) return;
          if (!localHangupRef.current && callIdRef.current) {
            const label = /busy/i.test(cause) ? 'Occupé' : /no.?answer|no_user/i.test(cause) ? 'Sans réponse' : 'Raccroché';
            setView((prev) => (prev ? { ...prev, phase: 'ended', detail: label } : prev));
            window.setTimeout(() => reset(), 1600);
          } else {
            reset();
          }
          if (id && sid && !serverDial) {
            void centreVoipService.hangup(sid, id).catch(() => {});
          }
          if (cause && cause !== 'NORMAL_CLEARING' && cause !== 'ORIGINATOR_CANCEL') {
            onError?.(cause);
          }
        }
      });

      return client;
    })().catch((error) => {
      dropClient();
      throw error;
    }).finally(() => {
      connectingRef.current = null;
    });

    return connectingRef.current;
  }, [audioEl, dropClient, enabled, onError, playRemote, reset, storeId, telnyxIds]);

  useEffect(() => {
    if (!enabled || !storeId) {
      dropClient();
      return undefined;
    }
    void ensureClient().catch((error) => {
      onError?.(error.message || 'Voix indisponible');
    });
    return undefined;
  }, [dropClient, enabled, ensureClient, onError, storeId]);

  useEffect(() => () => {
    dropClient();
  }, [dropClient]);

  const startCall = useCallback(async ({
    phone,
    name,
    orderId
  }: { phone: string; name?: string; orderId?: number | null }) => {
    if (!storeId || !phone) return;
    unlockCentreNotifSound();
    primeAudio();
    try {
      await unlockMic();
      const client = await ensureClient();
      if (!client) throw new Error('Voix indisponible');
      const creds = await centreVoipService.getToken(storeId);
      setView({
        id: 0,
        phone,
        name: name || phone,
        direction: 'out',
        phase: 'connecting',
        provider: 'voip'
      });
      autoAnswerRef.current = !isNativeAndroidApp();
      const placeLocally = isNativeAndroidApp();
      const started = await centreVoipService.startCall(storeId, {
        phone,
        contact_name: name,
        order_id: orderId,
        client_dial: placeLocally
      });
      callIdRef.current = started.call.id;
      serverDialRef.current = started.dial === 'server';
      waitCustomerRef.current = started.dial === 'server';
      setView({
        id: started.call.id,
        phone: started.call.phone,
        name: started.call.contact_name || name || phone,
        direction: 'out',
        phase: 'connecting',
        provider: 'voip'
      });
      if (started.dial === 'server') {
        if (sdkCallRef.current && sdkCallRef.current.state === 'ringing') {
          await sdkCallRef.current.answer({ remoteElement: audioEl() });
          playRemote(sdkCallRef.current);
        }
        return;
      }
      const destination = started.call.phone?.startsWith('+')
        ? started.call.phone
        : `+${started.call.phone}`;
      const call = client.newCall({
        destinationNumber: destination,
        callerNumber: creds.caller_id || started.from_number,
        audio: true,
        remoteElement: audioEl()
      });
      sdkCallRef.current = call;
    } catch (error: any) {
      reset();
      onError?.(error?.response?.data?.error?.message || error.message || 'Appel impossible');
    }
  }, [audioEl, ensureClient, onError, playRemote, primeAudio, reset, storeId, unlockMic]);

  const accept = useCallback(async () => {
    if (localInboundRef.current && sdkCallRef.current) {
      unlockCentreNotifSound();
      primeAudio();
      acceptingRef.current = true;
      setView((prev) => (prev ? { ...prev, phase: 'connecting' } : prev));
      try {
        await unlockMic();
        await sdkCallRef.current.answer({ remoteElement: audioEl() });
        playRemote(sdkCallRef.current);
      } catch (error: any) {
        reset();
        onError?.(error?.message || 'Décrochage impossible');
      }
      return;
    }
    if (!storeId || !callIdRef.current) return;
    unlockCentreNotifSound();
    primeAudio();
    acceptingRef.current = true;
    pendingAccept.current = true;
    autoAnswerRef.current = true;
    serverDialRef.current = true;
    setView((prev) => (prev ? { ...prev, phase: 'connecting' } : prev));
    const ringing = sdkCallRef.current?.state === 'ringing' ? sdkCallRef.current : null;
    if (ringing) {
      void Promise.resolve(ringing.answer({ remoteElement: audioEl() }))
        .then(() => playRemote(ringing))
        .catch(() => {});
      pendingAccept.current = false;
    }
    try {
      const ready = Promise.all([unlockMic(), ensureClient()]);
      const accepted = await centreVoipService.accept(storeId, callIdRef.current);
      await ready;
      if (accepted?.status === 'active') {
        setView((prev) => (prev && Number(prev.id) === Number(accepted.id) ? { ...prev, phase: 'active' } : prev));
      }
    } catch (error: any) {
      pendingAccept.current = false;
      const message = String(error?.response?.data?.error?.message || error.message || '');
      if (/outbound call/i.test(message)) return;
      const code = error?.response?.data?.error?.code;
      reset();
      if (code === 'CALL_TAKEN') {
        onError?.('Appel pris par un autre agent');
        return;
      }
      onError?.(error?.response?.data?.error?.message || error.message || 'Décrochage impossible');
    }
  }, [audioEl, ensureClient, onError, playRemote, primeAudio, reset, storeId, telnyxIds, unlockMic]);

  const reject = useCallback(async () => {
    const id = callIdRef.current;
    const sid = storeId;
    try {
      sdkCallRef.current?.hangup();
    } catch {
      /* closed */
    }
    reset();
    if (id && sid) {
      try {
        await centreVoipService.reject(sid, id);
      } catch {
        /* already closed */
      }
    }
  }, [reset, storeId]);

  const hangup = useCallback(async () => {
    localHangupRef.current = true;
    const id = callIdRef.current;
    const sid = storeId;
    try {
      sdkCallRef.current?.hangup();
    } catch {
      /* closed */
    }
    reset();
    if (id && sid) {
      try {
        await centreVoipService.hangup(sid, id);
      } catch {
        /* already closed */
      }
    }
  }, [reset, storeId]);

  const toggleMute = useCallback(() => {
    const call = sdkCallRef.current;
    if (!call) return;
    if (muted) call.unmuteAudio();
    else call.muteAudio();
    setMuted(!muted);
  }, [muted]);

  const toggleHold = useCallback(() => {
    const call = sdkCallRef.current;
    if (!call?.hold || !call.unhold) return;
    if (held) call.unhold();
    else call.hold();
    setHeld(!held);
    setView((prev) => (prev ? { ...prev, held: !held } : prev));
  }, [held]);

  useEffect(() => {
    if (!enabled || !storeId) return;
    const token = localStorage.getItem('accessToken') || accessToken;
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity
    });
    bindSocketTokenRefresh(socket);
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('store:join', storeId);
    });
    socket.on('whatsapp:call', (payload: {
      type?: string;
      provider?: string;
      call?: CentreCall;
    }) => {
      if (payload?.provider !== 'voip') return;
      const type = payload.type;
      const call = payload.call;
      if (type === 'incoming' && call) {
        if (acceptingRef.current || Number(callIdRef.current) === Number(call.id)) return;
        callIdRef.current = call.id;
        localInboundRef.current = false;
        serverDialRef.current = true;
        setView({
          id: call.id,
          phone: call.phone,
          name: call.contact_name || call.phone,
          direction: 'in',
          phase: 'incoming',
          provider: 'voip'
        });
        void ensureClient().catch(() => {});
        return;
      }
      if (type === 'identity' && call) {
        setView((prev) => {
          if (!prev || prev.id !== call.id) return prev;
          const nextName = call.contact_name || prev.name;
          return nextName === prev.name ? prev : { ...prev, name: nextName };
        });
        return;
      }
      if (type === 'ringing' && call) {
        setView((prev) => (
          prev && Number(prev.id) === Number(call.id) && prev.direction === 'out' && prev.phase !== 'active'
            ? { ...prev, phase: 'outgoing' }
            : prev
        ));
        return;
      }
      if (type === 'status' && call?.status === 'active') {
        playRemote(sdkCallRef.current || undefined);
        setView((prev) => (
          prev && Number(prev.id) === Number(call.id) ? { ...prev, phase: 'active' } : prev
        ));
        return;
      }
      if (type === 'taken' && call) {
        if (callIdRef.current && callIdRef.current !== call.id) return;
        if (acceptingRef.current) return;
        reset();
        return;
      }
      if (type === 'ended' && call) {
        if (callIdRef.current && callIdRef.current !== call.id) return;
        if (localHangupRef.current) {
          reset();
          return;
        }
        try {
          sdkCallRef.current?.hangup();
        } catch {
          /* closed */
        }
        const label = call.status === 'missed'
          ? 'Sans réponse'
          : call.status === 'rejected'
            ? 'Refusé'
            : call.status === 'failed'
              ? 'Échoué'
              : 'Raccroché';
        setView((prev) => (prev ? { ...prev, phase: 'ended', detail: label } : prev));
        window.setTimeout(() => reset(), 1600);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken, enabled, ensureClient, playRemote, reset, storeId]);

  useEffect(() => {
    if (!enabled || !storeId) return;
    const link = peekIncomingCallLink();
    if (!link || link.provider !== 'voip') return;
    if (link.storeId && link.storeId !== storeId) return;

    let cancelled = false;
    const restore = async () => {
      try {
        await ensureClient();
        const call = await centreVoipService.getCall(storeId, link.callId);
        if (cancelled) return;
        clearIncomingCallLink();
        if (!call || !['ringing', 'connecting'].includes(call.status)) {
          if (call?.status !== 'active') onError?.('Appel terminé');
          return;
        }
        callIdRef.current = call.id;
        setView({
          id: call.id,
          phone: call.phone,
          name: call.contact_name || call.phone,
          direction: 'in',
          phase: 'incoming',
          provider: 'voip'
        });
        if (link.action === 'answer') {
          window.setTimeout(() => { void accept(); }, 0);
        } else if (link.action === 'reject') {
          window.setTimeout(() => { void reject(); }, 0);
        }
      } catch {
        if (!cancelled) {
          clearIncomingCallLink();
          onError?.('Appel terminé');
        }
      }
    };
    void restore();
    return () => { cancelled = true; };
  }, [accept, enabled, ensureClient, onError, reject, storeId]);

  return {
    view,
    muted,
    held,
    startCall,
    accept,
    reject,
    hangup,
    toggleMute,
    toggleHold
  };
};
